import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/admin-design-system.css";
import "@/dccf-mobile-app.css";
import App from "@/App";

/**
 * Silence the benign "ResizeObserver loop completed with undelivered notifications"
 * warning emitted by Radix UI / shadcn / embla / recharts / framer-motion during
 * rapid layout transitions on mobile. This warning is NOT a real error — the
 * browser self-recovers on the next frame. However, react-scripts' dev error
 * overlay intercepts it and shows it as a fatal red screen.
 *
 * We:
 *  1. Swallow the specific message at window.error / window.unhandledrejection
 *     (scoped — any other error still propagates normally).
 *  2. Hide the dev error-overlay iframe IF it was triggered only by this message.
 *
 * References: https://github.com/WICG/resize-observer/issues/38
 *             https://github.com/facebook/create-react-app/issues/11334
 */
const RESIZE_OBSERVER_ERR_REGEX =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

const isResizeObserverError = (message) =>
  typeof message === "string" && RESIZE_OBSERVER_ERR_REGEX.test(message);

// Swallow it at the window-level so it never reaches react-error-overlay.
window.addEventListener("error", (event) => {
  if (isResizeObserverError(event.message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event?.reason;
  const message =
    typeof reason === "string" ? reason : reason?.message || "";
  if (isResizeObserverError(message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
});

// Also silence the corresponding console.error so it stops painting red text.
const originalConsoleError = console.error;
console.error = (...args) => {
  const first = args?.[0];
  const message = typeof first === "string" ? first : first?.message || "";
  if (isResizeObserverError(message)) return;
  originalConsoleError(...args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

/* PWA service worker — installable + network-first navigation, cache-first
   for static assets. API requests are always network for live web↔app sync. */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => { /* silent */ });
  });
}
